import { Pipe, PipeTransform } from "@angular/core";
import { Helper } from "../classes/helper.class";
import { FormControl } from "@angular/forms";
import { catchError, map, of, switchMap, tap } from "rxjs";
import { GlobalApiService } from "../services/global-api/global-api.service";

@Pipe({
  name: 'replace',
})
export class ReplaceCustomPipe implements PipeTransform {
  transform(currentString?: string | null, oldString?: string | null, newString?: string | null) {
    if (!currentString || !oldString) return currentString ?? '';
    return Helper.string.replaceAll(currentString, oldString, newString ?? '');
  }
}

@Pipe({
  name: 'formatDate',
})
export class FormatDateCustomPipe implements PipeTransform {
  transform(dateString: string) {
    if (!dateString) return '';
    return new Date(dateString).toDateString();
  }
}

@Pipe({
  name: 'formatTime',
})
export class FormatTimeCustomPipe implements PipeTransform {
  transform(timeString: string) {
    if (!timeString) return '';
    return new Date(`${new Date().toLocaleDateString()} ${timeString}`).toLocaleTimeString();
  }
}

@Pipe({
  name: 'formatDateTime',
})
export class FormatDateTimeCustomPipe implements PipeTransform {
  transform(dateTimeString?: string | Date | null) {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return `${date.toDateString()} at ${date.toLocaleTimeString()}`;
  }
}


@Pipe({
  name: 'invoker',
})
export class InvokerCustomPipe implements PipeTransform {
  /**
   * @Description A custom function invoker.
   */
  transform(func: Function) {
    return func();
  }
}

@Pipe({
  name: 'method',
})
export class MethodCustomPipe implements PipeTransform {
  /**
   * @Description A custom function invoker that accepts only one parameter.
   * @Suggestion Use `function` pipe to accept multiple parameters.
   * @Note Anything outside the function cannot be called.
   */
  transform(func: Function, param?: any, ...moreParam: any) {
    return func(param, moreParam);
  }
}

@Pipe({
  name: 'function',
})
export class FunctionCustomPipe implements PipeTransform {
  /**
   * @Description A custom function invoker that accepts multiple parameter. Parameters are returned as an array.
   * @Note Anything outside the function cannot be called.
   */
  transform(func: Function, ...params: any[]) {
    return func(params);
  }
}

@Pipe({
  name: 'stringify',
})
export class StringifyCustomPipe implements PipeTransform {
  /**
   * @Description run JSON.stringify() and return its value.
   */
  transform(value?: any) {
    if (!value) return '';
    const converted = JSON.stringify(value, null, " ");
    return converted == "{}" ? '' : converted;
  }
}

@Pipe({
  name: 'roundoff',
})
export class RoundoffCustomPipe implements PipeTransform {
  transform(value?: number | string | null, decimals: number = 2): string {
    if (value == null || value == undefined) return '';

    const num = Number(value);

    if (!Number.isFinite(num)) return String(num);

    return num.toFixed(decimals).replace(/\.?0+$/, '');
  }
}

@Pipe({
  name: 'evaluate',
})
export class EvaluateCustomPipe implements PipeTransform {
  transform(value: number | string | undefined) {
    if (!value) return '';
    return String(eval(String(value)));
  }
}

@Pipe({
  name: 'padStart',
})
export class PadStartCustomPipe implements PipeTransform {
  transform(value: string | number | undefined | null, maxLength: number, fillString: string) {
    if (!value) return '';
    return String(value).padStart(maxLength, fillString)
  }
}

@Pipe({
  name: 'padEnd',
})
export class PadEndCustomPipe implements PipeTransform {
  transform(value: string | undefined, maxLength: number, fillString: string) {
    if (!value) return '';
    return String(value).padEnd(maxLength, fillString)
  }
}

@Pipe({
  name: 'arrToStr',
})
export class ArrToStrCustomPipe implements PipeTransform {
  transform(value: any[] | null | undefined) {
    if (!value) return '';
    return Helper.string.replaceAll(value.toString(), ',', ', ');
  }
}

@Pipe({
  name: 'arrMap',
})
export class ArrMapCustomPipe implements PipeTransform {
  transform(value: any[] | null | undefined, key: string) {
    if (!value) return [];
    return value.map(v => v[key]);
  }
}

@Pipe({
  name: 'strLimit',
})
export class StrLimitCustomPipe implements PipeTransform {
  transform(value?: string | null, totalLength?: number | null) {
    if (!value) return '';
    value = String(value);
    const splicedLength = totalLength ? (totalLength > 0 ? totalLength : value.length) : value.length;
    const spliced = value.slice(0, splicedLength - 3);
    return spliced + (spliced.length < value.length ? '...' : '');
  }
}

@Pipe({
  name: 'valueAt',
})
export class ValueAtCustomPipe implements PipeTransform {
  transform(value?: string | unknown[], index?: number) {
    if (!value) return null;
    if (!index) return null;
    if (!value.length) return null;
    if (index >= value.length) return null;
    if (index < 0) return null;
    return value[index];
  }
}

@Pipe({
  name: 'split',
})
export class SplitCustomPipe implements PipeTransform {
  transform(value?: string | null, separator?: string | null) {
    if (!value) return [];
    if (!separator) return [value];
    return value.split(separator);
  }
}

@Pipe({
  name: 'objToArr',
})
export class ObjToArrCustomPipe implements PipeTransform {
  transform(value?: Object) {
    if (!value) return [];
    const pairs: { key: string, value: any }[] = [];
    Object.entries(value).forEach(([key, value]) => pairs.push({ key, value }));
    return pairs;
  }
}

@Pipe({
  name: 'consoleLog',
})
export class ConsoleLogCustomPipe implements PipeTransform {
  transform(value?: any, alias?: string | null) {
    if (alias) console.log({ [`${alias}`]: value });
    else console.log({ value });
    return value;
  }
}

@Pipe({
  name: 'uppcaseOn',
})
export class UppcaseOnCustomPipe implements PipeTransform {
  transform(str: string, index?: number | null) {
    if (index == undefined || index == null) return str;
    const char = str[index].toUpperCase();
    return `${str.substring(0, index)}${char}${str.substring(index + 1, str.length)}`;
  }
}

@Pipe({
  name: 'numberPlacement',
})
export class NumberPlacementCustomPipe implements PipeTransform {
  transform(place?: number | null): string {
    if (!place) return '';
    if (place < 1) return `${place}`;
    if (place == 1) return '1st'
    if (place == 2) return '2nd'
    if (place == 3) return '3rd'
    return `${place}th`
  }
}

@Pipe({
  name: 'invalidControl',
})
export class InvalidControlCustomPipe implements PipeTransform {
  transform(formControl?: FormControl | null): boolean {
    if (!formControl) return true;
    if (formControl.invalid && formControl.touched) return true;
    return false;
  }
}

@Pipe({
  name: 'arrayFirst',
})
export class ArrayFirstCustomPipe implements PipeTransform {
  transform<T>(array?: T[] | null, elementLimit?: number | null): T[] {
    if (!array) return [];
    return elementLimit ? array.slice(0, elementLimit) : array;
  }
}

@Pipe({
  name: 'joinPath',
})
export class JoinPathCustomPipe implements PipeTransform {
  transform(array?: string[] | null): string {
    if (!array) return '';
    return array.join('/');
  }
}

@Pipe({
  name: 'joinArr',
})
export class JoinArrCustomPipe implements PipeTransform {
  transform(array?: any[] | null, seperator?: string | null): string {
    if (!array) return '';
    array = array.filter((v?: string | null) => (!['', null, undefined].includes(v)));
    return array.join(seperator ?? '/');
  }
}

@Pipe({
  name: 'toStr',
})
export class ToStrCustomPipe implements PipeTransform {
  transform(value: any): string {
    return String(value);
  }
}

@Pipe({
  name: 'toFixed',
})
export class ToFixedCustomPipe implements PipeTransform {
  transform(value?: number | string | null, decimal: number = 2): string {
    const numVal = +(value ?? 0);
    return numVal.toFixed(decimal);
  }
}

@Pipe({
  name: 'printBillEmployee',
})
export class PrintBillEmployeeCustomPipe implements PipeTransform {
  transform(value?: string | null): string {
    if (!value) return '';
    value = value.replace('(', '');
    value = value.replace(')', '');
    value = value.replace('0', '');
    value = value.replace('1', '');
    value = value.replace('2', '');
    value = value.replace('3', '');
    value = value.replace('4', '');
    value = value.replace('5', '');
    value = value.replace('6', '');
    value = value.replace('7', '');
    value = value.replace('8', '');
    value = value.replace('9', '');
    return value;
  }
}

@Pipe({
  name: 'join',
})
export class JoinCustomPipe implements PipeTransform {
  transform(array?: (number | string)[], seperator: string = ' '): string {
    if (!array) return '';
    return array?.map(v => String(v)).reduce((c, n) => `${c}${seperator}${n}`, '');
  }
}

@Pipe({
  name: 'getProfilePic',
})
export class GetProfilePicCustomPipe implements PipeTransform {

  transform(fileName: string, directory: 'Receipts' | 'employeeImage' = 'employeeImage') {
    return this._getFile(fileName, directory);
  }

  constructor(
    private _globalS: GlobalApiService,
  ) { }

  private _getFile(fileName: string, directory: "Receipts" | "employeeImage") {
    return of(fileName).pipe(
      switchMap(values => this._globalS.getFileUrl(values, directory).pipe(
        map(values => values.body?.url),
        catchError(err => of(err).pipe(tap(console.error))),
      ))
    )
  }
}

@Pipe({
  name: 'sum',
})
export class SumCustomPipe implements PipeTransform {
  transform(array?: (number | string | null | undefined)[]): number {
    if (!array) return 0;

    return array.reduce((c, n): number => {
      if (c === null || c === undefined) c = 0;
      if (n === null || n === undefined) n = 0;

      if (typeof c === "string") c = Number(c);
      if (isNaN(c)) c = 0;

      if (typeof n === "string") n = Number(n);
      if (isNaN(n)) n = 0;

      return c + n;
    }, 0) as number;
  }
}

@Pipe({
  name: 'numberAmountToWords',
})
export class NumberAmountToWordsCustomPipe implements PipeTransform {
  transform(rawAmount?: number | string | null): string {
    if (!rawAmount) return 'Zero';

    const amount = Number(rawAmount);
    if (isNaN(amount)) return 'Zero';

    return Helper.number.numberToWords(amount);
  }
}

@Pipe({
  name: 'cleanArray',
})
export class CleanArrayCustomPipe implements PipeTransform {
  transform<T>(arr?: (T[]) | null): T[] {
    if (!arr) return [];
    return arr.filter(v => v !== null || v !== undefined);
  }
}

@Pipe({
  name: 'toNumber',
})
export class ToNumberCustomPipe implements PipeTransform {
  transform(value: any): number {
    return Number(value);
  }
}

@Pipe({
  name: 'isAll',
})
export class IsAllCustomPipe implements PipeTransform {
  transform<T>(arr?: T[] | null, compareValue?: T): boolean {
    if (!arr || arr.length === 0) return false;

    return arr.every((item) => item === compareValue);
  }
}